import React, { useEffect, useState } from 'react';
import { Mail, Link2, Loader2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { fetchWithAuth } from '../pages/IntelligenceLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const useGraphStatus = () => {
  const [status, setStatus] = useState({ configured: false, connected: false, mailbox: null, loading: true });

  const reload = () => {
    fetchWithAuth(`${API_URL}/api/graph/status`)
      .then((data) => setStatus({ ...data, loading: false }))
      .catch(() => setStatus({ configured: false, connected: false, mailbox: null, loading: false }));
  };

  useEffect(() => { reload(); }, []);
  return { status, reload };
};

// This app has a single-tier auth system — every admin_users record is
// role='admin' (backfilled + set on register/login). There is no 'viewer'
// account in production today. Treat a missing role (e.g. a session whose
// localStorage predates this field being added to the login response) as
// admin too, so an already-logged-in admin isn't silently blocked from
// seeing the Connect button until they happen to log out and back in.
export const isAdminUser = () => {
  try {
    const role = JSON.parse(localStorage.getItem('backoffice_user') || '{}').role;
    return role !== 'viewer';
  } catch {
    return true;
  }
};

export const connectMicrosoftMailbox = async () => {
  try {
    const res = await fetchWithAuth(`${API_URL}/api/graph/connect`);
    window.location.href = res.authorization_url;
  } catch (e) {
    toast.error('Could not start the Microsoft connection — you may need admin access.');
  }
};

// Compact inline status line — used inside the outreach composer.
export const GraphMailboxBadge = ({ status, onConnect }) => {
  if (status.loading) return null;
  if (!status.configured) {
    return (
      <div className="flex items-center gap-1.5 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5" data-testid="graph-not-configured">
        <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" />
        Microsoft Graph isn't configured yet — sending via Outlook is unavailable until Entra ID setup is complete.
      </div>
    );
  }
  if (!status.connected) {
    return (
      <div className="flex items-center justify-between gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5" data-testid="graph-not-connected">
        <span className="flex items-center gap-1.5"><Mail className="h-3.5 w-3.5 flex-shrink-0" /> Outlook mailbox not connected — sending is disabled.</span>
        {isAdminUser() && (
          <button onClick={onConnect} data-testid="graph-connect-btn" className="flex items-center gap-1 font-medium text-[#0A1F44] underline whitespace-nowrap">
            <Link2 className="h-3 w-3" /> Connect
          </button>
        )}
      </div>
    );
  }
  return (
    <div className="flex items-center gap-1.5 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-2.5 py-1.5" data-testid="graph-connected-badge">
      <Mail className="h-3.5 w-3.5 flex-shrink-0" /> Sending via <span className="font-medium">{status.mailbox || 'connected mailbox'}</span>
    </div>
  );
};

// Larger banner with connect/disconnect — used at the top of a CRM page.
export const GraphMailboxBanner = ({ status, onConnect, onDisconnect, disconnecting }) => {
  if (status.loading) return null;
  return (
    <div className="flex items-center justify-between gap-3 bg-white border border-gray-200 rounded-xl px-4 py-2.5 shadow-sm" data-testid="graph-mailbox-banner">
      <div className="flex items-center gap-2 text-sm">
        <Mail className="h-4 w-4 text-[#0A1F44]/60" />
        {!status.configured ? (
          <span className="text-gray-500">Microsoft Graph not configured — Outlook sending unavailable.</span>
        ) : status.connected ? (
          <span className="text-gray-700">Outlook mailbox connected: <span className="font-semibold text-[#0A1F44]">{status.mailbox}</span></span>
        ) : (
          <span className="text-gray-500">Outlook mailbox not connected — Send Now / Schedule Send disabled.</span>
        )}
      </div>
      {isAdminUser() && status.configured && (
        status.connected ? (
          <button onClick={onDisconnect} disabled={disconnecting} data-testid="graph-disconnect-btn"
            className="text-xs font-medium text-red-600 hover:underline disabled:opacity-50 flex items-center gap-1">
            {disconnecting && <Loader2 className="h-3 w-3 animate-spin" />} Disconnect
          </button>
        ) : (
          <button onClick={onConnect} data-testid="graph-connect-banner-btn"
            className="text-xs font-medium text-[#0A1F44] bg-[#fdf9ef] border border-[#D4AF37]/40 rounded-lg px-3 py-1.5 hover:bg-[#faf0d4] flex items-center gap-1">
            <Link2 className="h-3.5 w-3.5" /> Connect Outlook Mailbox
          </button>
        )
      )}
    </div>
  );
};
